
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProfile } from "@/hooks/useProfile";

export const useAvailableClasses = () => {
  const { profile } = useProfile();

  return useQuery({
    queryKey: ["classes", profile?.id, profile?.gym_id, profile?.role],
    queryFn: async () => {
      let query = supabase
        .from("classes")
        .select(`
          *,
          subscriptions:class_subscriptions(count)
        `);

      // Apply gym filtering based on user role
      if (profile?.role === 'admin') {
        // Admins can see all classes - no filter needed
      } else if (profile?.role === 'manager') {
        // Managers can see classes from gyms they manage
        const { data: managedGyms } = await supabase
          .from("gym_managers")
          .select("gym_id")
          .eq("manager_id", profile.id);
        
        if (managedGyms && managedGyms.length > 0) {
          const gymIds = managedGyms.map(gm => gm.gym_id);
          query = query.in("gym_id", gymIds);
        } else {
          // Manager with no gyms assigned - return empty array
          return [];
        }
      } else if (profile?.role === 'profesor') {
        // Professors - filter by their gym and (instructor name OR created_by)
        if (profile?.gym_id && profile?.first_name && profile?.last_name && profile?.id) {
          const instructorName = `${profile.first_name} ${profile.last_name}`;
          query = query
            .eq("gym_id", profile.gym_id)
            .or(`instructor.eq.${instructorName},created_by.eq.${profile.id}`);
        } else {
          // Professor not properly configured - return empty array
          return [];
        }
      } else {
        // Regular users - filter by their gym
        if (profile?.gym_id) {
          query = query.eq("gym_id", profile.gym_id);
        } else {
          // User not assigned to any gym - return empty array
          return [];
        }
      }

      const { data: classes, error: classesError } = await query
        .order("created_at", { ascending: false });

      if (classesError) {
        console.error("Error fetching classes:", classesError);
        throw classesError;
      }

      return classes?.map(class_ => ({
        ...class_,
        enrolledCount: class_.subscriptions[0]?.count || 0
      })) || [];
    },
    enabled: !!profile?.id,
  });
};
